import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { isUnifiedPositionEnabled } from "app/components/campaigns/shared/utils/isUnifiedPositionEnabled";
import { translate } from "app/filters/i18nFilter";
import {
    excludedFeaturesToOptions,
    getInitialExcludedFeatures,
} from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/edit-campaign-sections/excluded-features/utils";
import type { IKeywordsReportTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";

export const POSITION_TOOLTIP_I18N = {
    tabUnified: "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.position.tooltip.unified",
    includesAll: "rankTracker.keywordsReport.position.tooltip.includesAll",
    excludesOne: "rankTracker.keywordsReport.position.tooltip.excludesOne",
    excludesTwo: "rankTracker.keywordsReport.position.tooltip.excludesTwo",
    excludesThree: "rankTracker.keywordsReport.position.tooltip.excludesThree",
    excludesMany: "rankTracker.keywordsReport.position.tooltip.excludesMany",
    excludesManyOneOther: "rankTracker.keywordsReport.position.tooltip.excludesMany.oneOther",
    settings: "rankTracker.keywordsReport.position.tooltip.settings",
} as const;

const NAMED_FEATURES_LIMIT = 3;

export type PositionExcludedTooltipCopy = {
    i18nKey: string;
    values?: Record<string, string | number>;
};

export const getPositionExcludedTooltipCopy = (
    excluded: readonly ESerpTypes[] | null | undefined,
): PositionExcludedTooltipCopy => {
    const names = excludedFeaturesToOptions(getInitialExcludedFeatures(excluded)).map((option) => option.text);

    if (names.length === 0) {
        return { i18nKey: POSITION_TOOLTIP_I18N.includesAll };
    }
    if (names.length === 1) {
        return { i18nKey: POSITION_TOOLTIP_I18N.excludesOne, values: { feature: names[0] } };
    }
    if (names.length === 2) {
        return {
            i18nKey: POSITION_TOOLTIP_I18N.excludesTwo,
            values: { feature1: names[0], feature2: names[1] },
        };
    }
    if (names.length === 3) {
        return {
            i18nKey: POSITION_TOOLTIP_I18N.excludesThree,
            values: { feature1: names[0], feature2: names[1], feature3: names[2] },
        };
    }

    const remaining = names.length - NAMED_FEATURES_LIMIT;
    const values = {
        feature1: names[0],
        feature2: names[1],
        feature3: names[2],
        count: remaining,
    };

    if (remaining === 1) {
        return { i18nKey: POSITION_TOOLTIP_I18N.excludesManyOneOther, values };
    }

    return { i18nKey: POSITION_TOOLTIP_I18N.excludesMany, values };
};

export const getPositionMetricTooltip = (
    tabConfig: IKeywordsReportTableMetrics,
    fallback: string,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
): string => {
    if (!isUnifiedPositionEnabled() || tabConfig.metricID !== EKeywordsReportKeywordsTableMetrics.POSITION) {
        return fallback;
    }

    const copy = getPositionExcludedTooltipCopy(positionExcludedSerpFeatures);
    return `${translate(copy.i18nKey, copy.values)}\n\n${translate(POSITION_TOOLTIP_I18N.settings)}`;
};
